<script>
	import { onMount, createEventDispatcher } from 'svelte';
	import { fade } from 'svelte/transition';
    import Identity from 'svelte-key';
    import bbox from '@turf/bbox';
    import * as helpers from '@turf/helpers';
    import { hasCache, openSearchDb, getSearchCollection, closeSearchDb, search, cachedFetch, fetchData } from '../utils/Data.svelte';
    import { sortById, groupSort } from '../utils/Sort.svelte';
    import { addCircleOutline, trash, settings, grid, expand } from 'ionicons/icons/index.mjs';

    export let interactiveLayers = [];
    export let map;
    export let tooltips;
    export let session;
    let layer_id = "";
    let labelField = "";
    let layers = [];
    let selectedLayerId = "current";
    let selectedLayer = null;
    let items = [];
    let live = false;
    let schema = {
        fields: []
    };

    const dispatch = createEventDispatcher();
	function sendMessage(message) {
		dispatch('message', message);
    }

    onMount(() => {
        interactiveLayers.map(function (id) {
            var layer = map.getLayer(id);
            if (!layer)
                return;
            var source = "";
            if (layer.metadata && layer.metadata["geosync:searchable"] == false) {
                return;
            }
            if (layer.source) {
                source = layer.source.split("_")[0];
            }
            if (layer.metadata && layer.metadata["geosync:live"]) {
                layers.push({
                    id: layer.id,
                    source: layer.id
                })
            }
            else if (layer.metadata && layer.metadata["geosync:interactive"] && hasCache("source", source)) {
                layers.push({
                    id: layer.id,
                    source: "source_" + layer.id
                })
            }
            else if (layer.metadata && layer.metadata["geosync:interactive"] && hasCache("layer", layer.source)) {
                layers.push({
                    id: layer.id,
                    source: layer.id
                })
            }
        })
        layers = layers;
    });

    function layerChanged(e) {
        items = [];
        live = false;
        schema = {
            fields: []
        };
        var id = e.target.value;
        if (id == "current") {
            closeSearchDb();
        }
        else {
            var isSource = false;
            if (id.startsWith("source_")) {
                isSource = true;
                id = e.target.value.split("_")[1];
            }
            selectedLayer = map.getLayer(id);
            labelField = selectedLayer.metadata["geosync:label"];
            layer_id = selectedLayer.source;
            var source = selectedLayer.source.split("_")[0];
            if (selectedLayer.metadata["geosync:live"]) {
                live = true;
            }
            else {
                if (isSource)
                    openSearchDb("source_" + source);
                else {
                    openSearchDb("layer_" + selectedLayer.source);
                    source = selectedLayer.source;
                }
                
                cachedFetch("/v1/schemas/" + source, "schema", session.id, source, {
                    headers: {
                        'Authorization': 'Bearer ' + session.token
                    }
                }, async function ([res, err]) {
                    if (err) {
                        console.log(err);
                    }
                    else if (res) {
                        schema = res.data;
                    }
                })
            }
        }
    }

    async function searchHandler(e) {
        if (e.key === "Enter") {
            if (live) {
                var path = "/v1/search/" + layer_id;
                var body = {};
                if (e.target.value) {
                    body.query = e.target.value;
                }
                if (selectedLayer.filter) {
                    let condition = selectedLayer.filter[0] == "all" ? " AND " : " OR ";
                    let where = ""
                    selectedLayer.filter.map(function (filter, i) {
                        if (i == 0)
                            return;
                        if (i > 1) {
                            where = where + condition;
                        }
                        let operator = "=";
                        if (filter[0] != "==") {
                            operator = filter[0];
                        }
                        where = where + "[" + filter[1] + "] " + operator + " '" + filter[2] + "'"
                    })
                    body.filter = where;
                }
                let [res, err]  = await fetchData(path, {
                    method: "POST",
                    headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json',
                            'Authorization': 'Bearer ' + session.token
                    },
                    body: JSON.stringify(body)
                });
                if (!err) {
                    items = res.features;
                }
            }
            else {
                var filter = null;
                if (selectedLayer && selectedLayer.filter) {
                    filter = Array.from(selectedLayer.filter);
                }
                items = search(e.target.value, labelField, filter);
            }
        }
    }

    function zoomHandler(feature) {
        if (feature.geometry.type === "Point") {
        var zoom = 18;
        if (selectedLayer.metadata && selectedLayer.metadata["geosync:searchzoom"])
            zoom = selectedLayer.metadata["geosync:searchzoom"];
        map.flyTo({center: feature.geometry.coordinates, zoom: zoom});
        }
        else if (feature.geometry.type === "LineString") {
        var geometry = helpers.lineString(feature.geometry.coordinates);
        var bounds = bbox(geometry);
        map.fitBounds(bounds, {
            padding: 100
        });
        }
        else if (feature.geometry.type === "Polygon") {
        var geometry = helpers.polygon(feature.geometry.coordinates);
        var bounds = bbox(geometry);
        map.fitBounds(bounds, {
            padding: 100
        });
        }
        else if (feature.geometry.type === "MultiLineString") {
        var geometry = helpers.multiLineString(feature.geometry.coordinates);
        var bounds = bbox(geometry);
        map.fitBounds(bounds, {
            padding: 100
        });
        }
        else if (feature.geometry.type === "MultiPolygon") {
        var geometry = helpers.multiPolygon(feature.geometry.coordinates);
        var bounds = bbox(geometry);
        map.fitBounds(bounds, {
            padding: 100
        });
        }
        else if (feature.geometry.type === "MultiPoint") {
        var geometry = helpers.multiPoint(feature.geometry.coordinates);
        var bounds = bbox(geometry);
        map.fitBounds(bounds, {
          padding: 100
      });
    }
    sendMessage({
        type: "select",
        data: {
            feature: feature,
            layer: selectedLayer,
            label: feature.properties[labelField],
            idField: selectedLayer.metadata["geosync:id"]
        }
    })
    }
</script>

<div class="container" in:fade>
	<div class="tile tile-centered">
		<div class="tile-content">
            <div class="form-group pb-1">
                <select id="layerList" class="form-select" on:change={layerChanged} bind:value={selectedLayerId}>
                    <option value="current" selected>Current Features</option>
                    {#each layers as layer}
                        <option value={layer.source}>{layer.id}</option>
                    {/each}
                </select>
            </div>
        </div>
        <div class="tile-action">
            <button class="btn btn-link {tooltips ? "tooltip tooltip-left" : ""}" data-tooltip="Grid View" on:click={(e) => {
                    e.currentTarget.blur();
                    var collection = {
                        type: "FeatureCollection",
                        features: []
                    };
                    if (selectedLayerId == "current") {
                        collection.features = map.queryRenderedFeatures();
                    }
                    else {
                        var filter = null;
                        if (selectedLayer && selectedLayer.filter) {
                            filter = Array.from(selectedLayer.filter);
                        }
                        collection = getSearchCollection(filter);
                    }
                    
                    sendMessage({
                        type: "modal",
                        data: {
                            view: "grid",
                            source: collection,
                            layer: selectedLayer
                        }
                    })
                }}>
				<i class="svg">{@html grid.md}</i>
			</button>
        </div>
	</div>
    {#if schema.fields.length > 0}
        <div class="tile tile-centered">
            <div class="tile-content">
                <div class="form-group pb-1">
                    <select id="labelList" class="form-select" bind:value={labelField}>
                        {#each schema.fields as field}
                            <option value={field.key}>{field.key}</option>
                        {/each}
                    </select>
                </div>
            </div>
        </div>
    {/if}
    {#if selectedLayerId != "current"}
        <div class="tile tile-centered">
            <div class="tile-content">
                <div class="form-group pb-1">
                    <input class="form-input" type="text" autocomplete="off" id="search" on:keyup={searchHandler} placeholder="Search">
                </div>
            </div>
        </div>
    {/if}
    {#each items as item}
        <div class="tile tile-centered">
            <div class="tile-content">
                <div class="tile-title">{ item.properties[labelField] }</div>
            </div>
            <div class="tile-action">
                <button class="btn btn-link {tooltips ? "tooltip tooltip-left" : ""}" data-tooltip="View" on:click|stopPropagation={(e) => {
                            e.currentTarget.blur();
                            zoomHandler(item);
                        }}>
                    <i class="svg">{@html expand.md}</i>
                </button>
            </div>
        </div>
    {/each}
</div>

<style>
    ::-webkit-scrollbar {
		width: 6px;
		background-color: #f7f8f9;
	}
	::-webkit-scrollbar-thumb {
		background-color: rgba(122,129,132, 1);
    }
    .form-group {
        margin: 0.2rem 0;
    }
</style>